export const NOT_TODAY_REASON_OPTIONS = Object.freeze([
  {
    code: "medical",
    label: "Medical reason",
    shortLabel: "Medical",
    description: "Unsafe to treat today or medical clearance is needed.",
  },
  {
    code: "patient",
    label: "Patient asked to postpone",
    shortLabel: "Patient",
    description: "The patient chose to move the treatment to another day.",
  },
  {
    code: "time",
    label: "Ran out of time",
    shortLabel: "Time",
    description: "The visit ended before this treatment could be started.",
  },
  {
    code: "other",
    label: "Other reason",
    shortLabel: "Other",
    description: "A different reason prevented treatment today.",
  },
]);

const VALID_REASON_CODES = new Set(NOT_TODAY_REASON_OPTIONS.map((option) => option.code));

export function normalizeNotTodayReasonCode(value) {
  const code = String(value || "").trim().toLowerCase();
  return VALID_REASON_CODES.has(code) ? code : null;
}

export function notTodayReasonLabel(value, { short = false } = {}) {
  const code = normalizeNotTodayReasonCode(value);
  const option = NOT_TODAY_REASON_OPTIONS.find((candidate) => candidate.code === code);
  if (!option) return "Reason needed";
  return short ? option.shortLabel : option.label;
}

export function hasMedicalNotTodayDisposition(dispositions) {
  return Object.values(dispositions || {}).some((disposition) => (
    disposition?.kind === "not_today"
    && normalizeNotTodayReasonCode(disposition.reason_code) === "medical"
  ));
}

export function hasMissingNotTodayReason(dispositions) {
  return Object.values(dispositions || {}).some((disposition) => (
    disposition?.kind === "not_today"
    && !normalizeNotTodayReasonCode(disposition.reason_code)
  ));
}

export function deriveNoTreatmentReasonCode(dispositions) {
  const codes = [];

  for (const disposition of Object.values(dispositions || {})) {
    if (disposition?.kind === "not_today") {
      const code = normalizeNotTodayReasonCode(disposition.reason_code);
      if (!code) return null;
      codes.push(code);
    } else if (disposition?.kind === "declined") {
      codes.push("patient");
    }
  }

  if (codes.length === 0) return null;
  const unique = new Set(codes);
  return unique.size === 1 ? codes[0] : "other";
}

export function isNoPaymentVisitEligible({
  isCheckedIn,
  itemCount,
  doneItemCount,
  totalDueCents,
  finishBundleOnComplete = false,
  dispositions,
}) {
  if (!isCheckedIn || finishBundleOnComplete) return false;
  if (Number(doneItemCount || 0) !== 0) return false;
  if (Number(totalDueCents || 0) !== 0) return false;
  // A genuinely empty visit does not need a fabricated clinical reason.
  // The atomic checkout function accepts an empty items array, finalizes the
  // visit, and completes the appointment without issuing a receipt.
  if (Number(itemCount || 0) === 0 && Object.keys(dispositions || {}).length === 0) {
    return true;
  }
  return deriveNoTreatmentReasonCode(dispositions) !== null;
}
